import { useEffect, useCallback } from 'react';
import { Resource, Action } from '../Constants/socket';
import useSocketEvent from './useSocketEvent';

/**
 * Hook to listen for conversation-related events on the socket
 * and update the conversations accordingly.
 * 
 * @param {Object} params - Parameters object
 * @param {Function} params.setConversations - Function to update conversations
 * @param {Object|null} params.selectedConversation - Currently selected conversation
 * @param {Function} params.setSelectedConversation - Function to update selected conversation
 * @returns {boolean} Whether the socket is connected
 */
export default function useConversationSocketEvents({
  setConversations,
  selectedConversation,
  setSelectedConversation
}) {
  // Handle conversation create event
  const handleConversationCreate = useCallback((data) => {
    if (!data) return;
    
    setConversations(prevConversations => {
      // Check if conversation already exists
      const exists = prevConversations.some(conv => conv.id === data.id);
      if (exists) return prevConversations;
      
      // Add new conversation to the beginning of the list
      return [data, ...prevConversations];
    });
  }, [setConversations]);

  // Handle conversation update event
  const handleConversationUpdate = useCallback((data) => {
    if (!data) return;
    
    // Update in the conversations list
    setConversations(prevConversations => {
      const updatedConversations = prevConversations.map(conv => {
        if (conv.id === data.id) {
          // Important: For is_writing, we specifically check if it's false or true
          // This ensures we properly handle the typing indicator
          const updatedConv = { ...conv };
          
          // Update all received fields
          Object.keys(data).forEach(key => {
            updatedConv[key] = data[key];
          });
          
          return updatedConv;
        }
        return conv;
      });
      
      // Check if anything changed
      const hasChanges = JSON.stringify(updatedConversations) !== JSON.stringify(prevConversations);
      return hasChanges ? updatedConversations : prevConversations;
    });
    
    // Update selected conversation if it matches
    if (selectedConversation?.id === data.id) {
      setSelectedConversation(prev => {
        // Copy all fields from the previous object
        const updated = { ...prev };
        
        // Update all received fields
        Object.keys(data).forEach(key => {
          updated[key] = data[key];
        });
        
        return updated;
      });
    }
  }, [setConversations, selectedConversation, setSelectedConversation]);

  // Handle conversation delete event
  const handleConversationDelete = useCallback((data) => {
    if (!data) return;
    
    setConversations(prevConversations => {
      const filteredConversations = prevConversations.filter(conv => conv.id !== data.id);
      
      // If the deleted conversation is the selected one, select another conversation or clear it
      if (selectedConversation?.id === data.id) {
        if (filteredConversations.length > 0) {
          // Find index of deleted conversation in the original array
          const deletedIndex = prevConversations.findIndex(conv => conv.id === data.id);
          
          // Try to select the next conversation or the previous one if we're at the end
          const nextIndex = deletedIndex < filteredConversations.length ? deletedIndex : deletedIndex - 1;
          // Make sure we have a valid index
          const newSelectedIndex = Math.max(0, Math.min(nextIndex, filteredConversations.length - 1));
          
          // Select the new conversation
          setTimeout(() => {
            if (filteredConversations[newSelectedIndex]) {
              setSelectedConversation(filteredConversations[newSelectedIndex]);
            }
          }, 0);
        } else {
          // No conversations left, clear selected conversation
          setSelectedConversation(null);
        }
      }
      
      return filteredConversations;
    });
  }, [setConversations, selectedConversation, setSelectedConversation]);

  // Handle message create event - update conversation with new message
  const handleMessageCreate = useCallback((data) => {
    if (!data || !data.conversation_id) return;
    
    const conversationId = data.conversation_id;
    
    setConversations(prevConversations => {
      return prevConversations.map(conv => {
        if (conv.id === conversationId) {
          // Update conversation with new last message
          return {
            ...conv,
            last_message: {
              content: data.content,
              sender_type: data.sender_type,
              sent_at: data.sent_at,
              status: {
                delivered: Boolean(data.delivered_at),
                delivered_at: data.delivered_at,
                read: Boolean(data.read_at),
                read_at: data.read_at,
                failed: Boolean(data.failed_at),
                failed_at: data.failed_at
              }
            },
            updated_time: data.sent_at // Update conversation timestamp
          };
        }
        return conv;
      });
    });
    
    // Update selected conversation if applicable
    if (selectedConversation?.id === conversationId) {
      // Note: we're not updating the messages array here,
      // that would be handled by a separate hook for messages
      setSelectedConversation(prev => ({
        ...prev,
        last_message: {
          content: data.content,
          sender_type: data.sender_type,
          sent_at: data.sent_at,
          status: {
            delivered: Boolean(data.delivered_at),
            delivered_at: data.delivered_at,
            read: Boolean(data.read_at),
            read_at: data.read_at,
            failed: Boolean(data.failed_at),
            failed_at: data.failed_at
          }
        },
        updated_time: data.sent_at
      }));
    }
  }, [setConversations, selectedConversation, setSelectedConversation]);

  // Set up socket event listeners
  useSocketEvent(Resource.CONVERSATION, Action.CREATE, handleConversationCreate);
  useSocketEvent(Resource.CONVERSATION, Action.UPDATE, handleConversationUpdate);
  useSocketEvent(Resource.CONVERSATION, Action.DELETE, handleConversationDelete);
  useSocketEvent(Resource.MESSAGE, Action.CREATE, handleMessageCreate);
} 